/**
 * @file OrderCard.tsx
 * @description High-contrast, touch-optimized ticket card for kitchen line displays.
 */

import React from 'react';
import { ChefHat, CheckCircle2, User, AlertTriangle, Clock } from 'lucide-react';
import { OrderDelayBadge } from './OrderDelayBadge';
import { OrderPriorityBadge } from './OrderPriorityBadge';
import { OrderItemRow } from './OrderItemRow';
import type { KitchenOrder, KitchenOrderStatus, KitchenPriority } from '../../../types/order.types';

interface OrderCardProps {
  order: KitchenOrder;
  onStatusChange: (orderId: string, status: KitchenOrderStatus) => void;
  onPriorityToggle: (orderId: string, priority: KitchenPriority) => void;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, onStatusChange, onPriorityToggle }) => {
  const getNextAction = () => {
    switch (order.status) {
      case 'new':
        // If the waiter has not confirmed the order to the kitchen yet, chef only sees unclickable WAITING
        if (!order.is_confirmed_to_kitchen) {
          return {
            label: 'WAITING (AWAITING WAITER)',
            targetStatus: 'preparing' as KitchenOrderStatus,
            colorClass: 'bg-zinc-800/70 text-zinc-500 border border-zinc-700/40 cursor-not-allowed',
            icon: Clock,
            disabled: true,
          };
        }

        // When waiter confirms to kitchen, chef can click START PREPARATION to begin cooking
        return {
          label: 'START PREPARATION',
          targetStatus: 'preparing' as KitchenOrderStatus,
          colorClass: 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/30 cursor-pointer',
          icon: ChefHat,
          disabled: false,
        };
      case 'preparing':
        return {
          label: 'READY FOR WAITER',
          targetStatus: 'ready' as KitchenOrderStatus,
          colorClass: 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30 cursor-pointer',
          icon: CheckCircle2,
          disabled: false,
        };
      case 'ready':
        // The food is ready at the pass. Only the waiter can confirm if served to table.
        return null;
      default:
        return null;
    }
  };

  const action = getNextAction();

  const handlePriorityToggle = () => {
    const nextPriority: KitchenPriority = order.priority === 'rush' ? 'normal' : 'rush';
    onPriorityToggle(order.id, nextPriority);
  };

  const timeReceived = new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Prevents duplicate "TABLE Table" prefixing and normalizes takeaway/bar labels
  const displayTable = React.useMemo(() => {
    const raw = String(order.table_number || '').trim();
    if (/^table\s*/i.test(raw)) {
      return raw.toUpperCase();
    }
    if (/^(takeaway|delivery|bar|counter)/i.test(raw)) {
      return raw.toUpperCase();
    }
    return `TABLE ${raw.toUpperCase()}`;
  }, [order.table_number]);

  return (
    <div className={`flex flex-col rounded-2xl bg-zinc-950 border transition-all duration-200 ${
      order.priority === 'rush'
        ? 'border-rose-500/80 shadow-2xl shadow-rose-950/40 ring-1 ring-rose-500/40'
        : order.status === 'ready'
        ? 'border-emerald-500/60 shadow-lg shadow-emerald-950/20'
        : 'border-zinc-800/80 hover:border-zinc-700'
    }`}>
      {/* Ticket Header */}
      <div className="p-3.5 border-b border-zinc-800/80 bg-zinc-900/40 rounded-t-2xl flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-black text-white tracking-tight">
              {displayTable}
            </span>
            <span className="text-xs font-mono font-bold text-zinc-400 bg-zinc-800/80 px-1.5 py-0.5 rounded">
              {order.order_number}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
            <span>{order.guest_count} guests</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <User className="w-3 h-3 text-zinc-500" />
              {order.waiter_name}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono font-semibold text-zinc-400">{timeReceived}</span>
            <OrderDelayBadge
              createdAt={order.created_at}
              confirmedAt={order.confirmed_to_kitchen_at}
              readyAt={order.ready_at}
              status={order.status}
            />
          </div>
          {order.status !== 'done' && (
            <OrderPriorityBadge priority={order.priority} onTogglePriority={handlePriorityToggle} />
          )}
        </div>
      </div>

      {/* Allergens Alert Banner */}
      {order.allergens && order.allergens.length > 0 && (
        <div className="px-3.5 py-1.5 bg-rose-950/70 border-b border-rose-900/60 flex items-center gap-1.5 text-xs font-bold text-rose-300">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
          <span>ALLERGENS: {order.allergens.join(', ')}</span>
        </div>
      )}

      {/* Customer / Table Notes */}
      {order.customer_notes && (
        <div className="px-3.5 py-1.5 bg-amber-950/30 border-b border-amber-900/40 text-xs text-amber-300 font-medium">
          Special Note: {order.customer_notes}
        </div>
      )}

      {/* Order Items List */}
      <div className="p-3 space-y-1.5 flex-1 max-h-[360px] overflow-y-auto">
        {order.items.map(item => (
          <OrderItemRow key={item.id} item={item} />
        ))}
      </div>

      {/* Action Footer: Chef can start prep or mark ready. Serving to table is reserved strictly for Waiters. */}
      {action ? (
        <div className="p-3 border-t border-zinc-800/80 bg-zinc-900/20 rounded-b-2xl">
          <button
            type="button"
            disabled={action.disabled}
            onClick={() => {
              if (!action.disabled) {
                onStatusChange(order.id, action.targetStatus);
              }
            }}
            className={`w-full py-3.5 px-4 rounded-xl font-black text-xs tracking-wider uppercase flex items-center justify-center gap-2 transition-all ${
              action.disabled ? 'opacity-60 pointer-events-none' : 'active:scale-[0.98]'
            } ${action.colorClass}`}
          >
            <action.icon className="w-4 h-4" />
            <span>{action.label}</span>
          </button>
        </div>
      ) : order.status === 'ready' ? (
        <div className="p-3 border-t border-emerald-900/50 bg-emerald-950/30 rounded-b-2xl flex items-center justify-center gap-2 text-xs font-black tracking-wide text-emerald-400">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>PLATED AT PASS • AWAITING WAITER PICKUP</span>
        </div>
      ) : order.status === 'done' ? (
        <div className="p-3 border-t border-zinc-800/80 bg-zinc-900/30 rounded-b-2xl flex items-center justify-center gap-2 text-xs font-black tracking-wide text-zinc-400">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>SERVED TO TABLE</span>
        </div>
      ) : null}
    </div>
  );
};
